import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { Vocabulary } from '@/utils/vocabulary'
import {
  generateId,
  saveVocabularyStore,
  loadVocabularyStore,
  getWordsForReview,
  getTodayNewWords
} from '@/utils/vocabulary'

/**
 * 生词本状态管理
 */
export const useVocabularyStore = defineStore('vocabulary', () => {
  // 状态
  const vocabulary = ref<Vocabulary[]>([])
  const settings = ref({
    dailyNewWords: 6,
    reviewThreshold: 5
  })
  
  // 加载持久化数据
  function load() {
    const stored = loadVocabularyStore()
    if (stored) {
      vocabulary.value = stored.vocabulary || []
      settings.value = stored.settings || settings.value
    }
  }

  // 添加新词汇
  function addVocabulary(
    word: string,
    meaning: string,
    phonetic?: string,
    example?: string,
    sourceText?: string,
    scenario?: string
  ): Vocabulary {
    const newWord: Vocabulary = {
      id: generateId(),
      word,
      meaning,
      phonetic,
      example,
      sourceText: sourceText || '',
      scenario: scenario || 'general',
      createdAt: Date.now(),
      masteryLevel: 0,
      nextReview: Date.now(),
      reviewCount: 0
    }
    
    vocabulary.value.push(newWord)
    saveStore()
    return newWord
  }

  // 更新掌握等级
  function updateMastery(wordId: string, correct: boolean): void {
    const word = vocabulary.value.find(v => v.id === wordId)
    if (!word) return

    const oldLevel = word.masteryLevel
    word.masteryLevel = correct
      ? Math.min(oldLevel + 1, 5)
      : Math.max(oldLevel - 1, 0)
    word.reviewCount += 1
    
    // 计算下次复习时间
    word.nextReview = calculateNextReviewTime(word.masteryLevel)
    
    saveStore()
  }

  // 计算下次复习时间
  function calculateNextReviewTime(masteryLevel: number): number {
    const intervals = [1, 2, 4, 7, 15, 30]
    const days = intervals[Math.min(masteryLevel, intervals.length - 1)]
    return Date.now() + days * 24 * 60 * 60 * 1000
  }

  // 获取待复习词汇
  const wordsForReview = computed(() => getWordsForReview(vocabulary.value))

  // 获取今天新词
  const todayNewWords = computed(() =>
    getTodayNewWords(vocabulary.value, settings.value.dailyNewWords)
  )

  // 统计数据
  const stats = computed(() => ({
    total: vocabulary.value.length,
    mastered: vocabulary.value.filter(v => v.masteryLevel >= 4).length,
    learning: vocabulary.value.filter(v => v.masteryLevel > 0 && v.masteryLevel < 4).length,
    new: vocabulary.value.filter(v => v.masteryLevel === 0).length,
    todayReview: wordsForReview.value.length
  }))

  // 保存到localStorage
  function saveStore(): void {
    saveVocabularyStore({
      vocabulary: vocabulary.value,
      settings: settings.value
    })
  }

  // 删除词汇
  function removeVocabulary(wordId: string): void {
    const index = vocabulary.value.findIndex(v => v.id === wordId)
    if (index !== -1) {
      vocabulary.value.splice(index, 1)
      saveStore()
    }
  }

  // 清空所有词汇
  function clearAll(): void {
    vocabulary.value = []
    saveStore()
  }

  // 初始化时加载
  load()

  return {
    // 状态
    vocabulary,
    settings,
    // 计算属性
    wordsForReview,
    todayNewWords,
    stats,
    // 方法
    load,
    addVocabulary,
    updateMastery,
    removeVocabulary,
    clearAll,
    saveStore
  }
})
